import { ReactNode, useEffect } from 'react';

export function Spinner(): JSX.Element {
  return <div className="spinner" aria-label="Loading" />;
}

export function Avatar({ emoji, large }: { emoji: string; large?: boolean }): JSX.Element {
  return <div className={large ? 'avatar lg' : 'avatar'}>{emoji}</div>;
}

export function Bar({ value, tone }: { value: number; tone?: 'good' | 'warn' | 'bad' }): JSX.Element {
  return (
    <div className={`bar${tone ? ` ${tone}` : ''}`}>
      <span style={{ width: `${Math.max(0, Math.min(100, value * 100))}%` }} />
    </div>
  );
}

export function Ring({ value, tone }: { value: number; tone?: 'good' | 'warn' | 'bad' }): JSX.Element {
  const colour = tone === 'good' ? 'var(--good)' : tone === 'bad' ? 'var(--bad)' : tone === 'warn' ? 'var(--warn)' : 'var(--accent)';
  return (
    <div
      className="ring"
      style={{ ['--value' as string]: Math.round(value * 100), ['--ring-color' as string]: colour }}
    >
      <span>{Math.round(value * 100)}%</span>
    </div>
  );
}

export function Empty({ icon, children }: { icon: string; children: ReactNode }): JSX.Element {
  return (
    <div className="empty">
      <span className="big">{icon}</span>
      {children}
    </div>
  );
}

export function Sheet({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
}): JSX.Element {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  return (
    <div className="sheet-backdrop" onClick={onClose} role="presentation">
      <div className="sheet" onClick={(e) => e.stopPropagation()} role="dialog" aria-label={title}>
        <div className="row between" style={{ marginBottom: 12 }}>
          <h3 style={{ margin: 0 }}>{title}</h3>
          <button className="btn ghost" onClick={onClose} type="button">
            Close
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function ErrorNote({ error }: { error: string | null }): JSX.Element | null {
  if (!error) return null;
  return <div className="notice bad" style={{ marginBottom: 12 }}>{error}</div>;
}
